// Website inbox (0.6): what citizens and visitors send to an advisor through avrastro.com — a permissioned share of
// saved reports, an appointment request or a message — becomes CRM data ONLY when the advisor accepts it.
// A citizen account is never a CRM record by itself. Accepting:
//   - links to an existing person (same e-mail or phone, or the advisor's choice) or creates a lead/prospect;
//   - share   → consent record (purpose "advice", lawful basis consent, scope = what was shared, until when),
//               a note with the shared results, a follow-up task;
//   - booking → an appointment task at the requested time (linked to the website booking), a note;
//   - message → a note and a reply task; a call-back request (kind callback) → a call task at the time of day that
//               suits (ideas track 8: the «Πάρτε με τηλέφωνο» form on the advisor's own site).
// A citizen withdrawing a share is recorded as a NEW consent record (history is append-only).
import { mergeChangesets, type Changeset, type WorkspaceView } from './changeset.ts';
import { DomainError, clean, put, timeline, touched, type Ctx } from './cmd.ts';
import { addNote, createPerson, createTask } from './services.ts';
import { recordConsent } from './advisory.ts';
import type { Person, PersonStage } from './schema.ts';
import { addDays } from './dates.ts';
import { parseRiskProfileShare, saveRiskProfile } from './risk-profile.ts';

export type IntakeKind = 'share' | 'booking' | 'lead';
export interface IntakeResult { tool?: string; toolTitle?: string; title?: string; createdAt?: string; result?: { headline?: { label?: string; value?: string }; lines?: { label?: string; value?: string }[]; insights?: string[] } | null }
export interface IntakePayload {
  citizen?: string | null; name?: string; email?: string; phone?: string;
  profile?: { name?: string; email?: string; phone?: string };
  purpose?: string; message?: string; topic?: string; topicLabel?: string; mode?: string; note?: string; kind?: string;
  results?: IntakeResult[]; allowPortal?: boolean; expiresAt?: string; createdAt?: string;
  startsAt?: string; endsAt?: string; status?: string; lang?: string;
  /** 1.4: where the appointment takes place (the advisor's office, or the video link) */
  location?: string; mapUrl?: string;
  /** ideas track 2: the site the booking was made on when it was not avrastro.com itself (e.g. farazisgiannis.com) */
  site?: string;
  /** ideas track 8: a call-back request says when a call suits */
  bestTime?: string;
  /** 1.6: general insurance — the licensed partner who gives the advice (name, firm, registration number) */
  partner?: string;
  revoked?: boolean;
  /** ideas track 18: policies from the citizen's own folder, shared with «Θέλω βοήθεια» */
  policies?: IntakePolicy[];
  /** ideas track 19: a second opinion on a quote — the question and a signed link to the file on the website (it works
   *  only while the citizen's share is active; the file itself is never copied into the payload) */
  opinion?: { question?: string; fileName?: string; fileMime?: string; fileSize?: number; fileUrl?: string | null; fileUntil?: string };
}
export interface IntakePolicy { insurer?: string; type?: string; typeLabel?: string; label?: string; renewsOn?: string; sumInsured?: number | null; premium?: number | null; premiumPeriod?: string | null; notes?: string }

/** plain-text lines for policies the citizen shared from their folder (insurer, type, renewal date, amounts) */
export function sharedPoliciesText(policies: IntakePolicy[] | undefined, labels: { renews?: string; sum?: string; premium?: string } = {}): string {
  return (policies ?? []).slice(0, 20).map((x) => {
    const head = [clean(x.typeLabel) || clean(x.type), clean(x.insurer), clean(x.label)].filter(Boolean).join(' · ');
    const bits = [x.renewsOn ? `${labels.renews ?? 'Renews'}: ${String(x.renewsOn).slice(0, 10)}` : '',
      typeof x.sumInsured === 'number' ? `${labels.sum ?? 'Sum insured'}: €${x.sumInsured.toLocaleString('en-US')}` : '',
      typeof x.premium === 'number' ? `${labels.premium ?? 'Premium'}: €${x.premium.toLocaleString('en-US')}${x.premiumPeriod ? ` / ${x.premiumPeriod}` : ''}` : ''].filter(Boolean);
    return `• ${head || 'Policy'}${bits.length ? `\n   ${bits.join(' · ')}` : ''}${clean(x.notes) ? `\n   ${clean(x.notes)}` : ''}`;
  }).join('\n');
}
export interface IntakeInput { id: string; ref: string; kind: IntakeKind; receivedAt: string; payload: IntakePayload }

/** the website a booking or message came from: avrastro.com unless the payload names a known partner site */
export function intakeSite(p: IntakePayload): string {
  const s = String(p.site ?? '').toLowerCase().replace(/^www\./, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s) && s.length <= 60 ? s : 'avrastro.com';
}

const digits = (s: unknown) => String(s ?? '').replace(/\D/g, '').replace(/^00/, '').replace(/^357(?=\d{8}$)/, '');

/** who the item is about, whatever the kind */
export function intakeContact(p: IntakePayload): { name: string; email: string; phone: string } {
  return { name: clean(p.profile?.name ?? p.name), email: clean(p.profile?.email ?? p.email).toLowerCase(), phone: clean(p.profile?.phone ?? p.phone) };
}

/** an existing person with the same e-mail or phone (never by name alone: names are not identities) */
export function matchIntakePerson(v: WorkspaceView, p: IntakePayload): Person | null {
  const c = intakeContact(p);
  const live = v.rows('persons');
  if (c.email) {
    const hit = live.find((x) => (x.primaryEmail ?? '').toLowerCase() === c.email || (x.emails ?? []).some((e) => e.value.toLowerCase() === c.email));
    if (hit) return hit;
  }
  const d = digits(c.phone);
  if (d.length >= 8) {
    const hit = live.find((x) => digits(x.primaryPhone) === d || (x.phones ?? []).some((ph) => digits(ph.value) === d));
    if (hit) return hit;
  }
  if (p.citizen) {
    const hit = live.find((x) => (x.data as { website?: { citizen?: string } } | null)?.website?.citizen === p.citizen);
    if (hit) return hit;
  }
  return null;
}

/** local calendar day and time of an instant, in the advisor's time zone */
export function localDateTime(iso: string, timeZone?: string | null): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) throw new DomainError('intake.bad_time');
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  const parts = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}` };
}

/** plain-text summary of the shared results (what the advisor reads in the note) */
export function sharedResultsText(results: IntakeResult[] | undefined): string {
  return (results ?? []).map((r) => {
    const head = [r.toolTitle, r.title && r.title !== r.toolTitle ? `“${r.title}”` : ''].filter(Boolean).join(' ');
    const res = r.result ?? null;
    const lines = [res?.headline?.label && res.headline.value ? `${res.headline.label}: ${res.headline.value}` : '', ...(res?.lines ?? []).slice(0, 12).map((l) => `${l.label ?? ''}: ${l.value ?? ''}`)].filter(Boolean);
    const ins = (res?.insights ?? []).slice(0, 4).map((x) => `→ ${x}`);
    return [`• ${head || r.tool || 'Report'}${r.createdAt ? ` (${r.createdAt.slice(0, 10)})` : ''}`, ...lines.map((l) => `   ${l}`), ...ins.map((l) => `   ${l}`)].join('\n');
  }).join('\n');
}

export interface AcceptIntakeOptions {
  /** link to this person instead of matching or creating */
  personId?: string | null;
  /** 1.4: the advisor said this is someone new (no matching by e-mail or phone) */
  createNew?: boolean;
  /** stage of a newly created person (default: lead for messages, prospect otherwise) */
  stage?: PersonStage;
  timeZone?: string | null;
  /** localized texts (the UI passes them; tests use the defaults) */
  text?: Partial<Record<'shareNote' | 'shareTask' | 'bookingNote' | 'bookingConfirmed' | 'leadNote' | 'leadTask' | 'callbackTask' | 'scope' | 'bestTimeMorning' | 'bestTimeMidday' | 'bestTimeAfternoon' | 'opinionFile', string>>;
  /** 1.4: where an office appointment takes place when the website did not say (the advisor's own office address) */
  officeAddress?: string | null;
}

/** ideas track 8: the time of day a call-back request asked for → the call task's time (the start of that window) */
export const CALLBACK_TIMES = { morning: '09:00', midday: '12:00', afternoon: '15:00' } as const;
export function callbackTime(bestTime: unknown): string | null {
  return Object.prototype.hasOwnProperty.call(CALLBACK_TIMES, String(bestTime)) ? CALLBACK_TIMES[bestTime as keyof typeof CALLBACK_TIMES] : null;
}

/** the CRM's meeting type for a website booking mode (the website says "video" for a video call) */
export function bookingLocationType(mode: unknown): 'office' | 'online' | 'phone' | 'client' | 'custom' {
  // 1.6: general insurance meets at a café the client names (custom place) or at the client's own place
  if (mode === 'cafe') return 'custom';
  if (mode === 'client_place') return 'client';
  return mode === 'video' || mode === 'online' ? 'online' : mode === 'phone' ? 'phone' : 'office';
}

/** accept an inbox item into the CRM; returns one changeset and the person it belongs to */
export function acceptIntake(v: WorkspaceView, item: IntakeInput, o: AcceptIntakeOptions, ctx: Ctx): { cs: Changeset; personId: string; created: boolean } {
  const p = item.payload ?? {};
  if (p.revoked) throw new DomainError('intake.revoked');
  const c = intakeContact(p);
  const parts: Changeset[] = [];
  let person = o.personId ? v.get('persons', o.personId) ?? null : o.createNew ? null : matchIntakePerson(v, p);
  if (o.personId && !person) throw new DomainError('person.not_found');
  let created = false;
  if (!person) {
    if (!c.name && !c.email && !c.phone) throw new DomainError('intake.no_contact');
    const cs = createPerson(v, { displayName: c.name || c.email || c.phone, primaryEmail: c.email || undefined, primaryPhone: c.phone || undefined, stage: o.stage ?? (item.kind === 'lead' ? 'lead' : 'prospect'), source: intakeSite(p) }, ctx);
    const row = cs.puts.find((x) => x.table === 'persons')!.row as Person;
    (row as { data: unknown }).data = { ...(row.data as object | null ?? {}), website: { citizen: p.citizen ?? null, refs: [item.ref] } };
    parts.push(cs);
    person = row;
    created = true;
  } else {
    // remember the website link on the person (no other field is overwritten)
    const web = (person.data as { website?: { citizen?: string | null; refs?: string[] } } | null)?.website ?? {};
    const refs = [...new Set([...(web.refs ?? []), item.ref])];
    parts.push({ action: 'person.update', puts: [put('persons', touched(ctx, person, { data: { ...(person.data as object | null ?? {}), website: { citizen: web.citizen ?? p.citizen ?? null, refs } } }))], deletes: [] });
  }
  const view = overlay(v, parts);
  const pid = person.id;
  const T = o.text ?? {};
  if (item.kind === 'share') {
    const n = (p.results ?? []).length;
    const fields = Object.keys(p.profile ?? {}).join(', ');
    const scope = (T.scope ?? 'avrastro.com share {ref}: {n} report(s); profile: {fields}; client portal: {portal}; until {until}')
      .replace('{ref}', item.ref).replace('{n}', String(n)).replace('{fields}', fields || '—').replace('{portal}', p.allowPortal ? 'yes' : 'no').replace('{until}', (p.expiresAt ?? '').slice(0, 10) || '—');
    parts.push(recordConsent(view, { personId: pid, purpose: 'advice', granted: true, channel: 'website', lawfulBasis: 'consent', scope, source: 'avrastro.com', date: (p.createdAt ?? item.receivedAt).slice(0, 10), notes: p.purpose ? `${p.purpose}` : undefined }, ctx));
    const quote = p.opinion?.fileName ? `${T.opinionFile ?? 'Quote for a second opinion'}: ${clean(p.opinion.fileName)}` : '';
    const body = [p.message ? `“${clean(p.message)}”` : '', quote, sharedPoliciesText(p.policies), sharedResultsText(p.results)].filter(Boolean).join('\n\n');
    parts.push(addNote(view, { personId: pid, title: (T.shareNote ?? 'Shared through avrastro.com: {purpose}').replace('{purpose}', p.purpose ?? ''), body: body || '—' }, ctx));
    // v12: a shared risk-profile result becomes a dated risk profile in the client's file (source: website)
    (p.results ?? []).forEach((r, i) => {
      const rp = parseRiskProfileShare(r);
      const ref = `${item.ref}:${i}`;
      const vw = overlay(v, parts);
      if (!rp || vw.where('risk_profiles', 'personId', pid).some((x) => x.sourceRef === ref)) return;
      const date = rp.date && rp.date <= ctx.today ? rp.date : (p.createdAt ?? item.receivedAt).slice(0, 10) <= ctx.today ? (p.createdAt ?? item.receivedAt).slice(0, 10) : ctx.today;
      parts.push(saveRiskProfile(vw, { personId: pid, source: 'website', date, level: rp.level, score: rp.score, maxScore: rp.maxScore, importedAnswers: rp.answers, horizonYears: rp.horizonYears, sourceVersion: rp.sourceVersion, sourceRef: ref, notes: rp.title }, ctx));
    });
    parts.push(withData(createTask(view, { kind: 'followup', title: (T.shareTask ?? 'Review what {name} shared').replace('{name}', person.displayName), personId: pid, dueDate: addDays(ctx.today, 2), priority: 'high', origin: 'website' }, ctx), item.ref));
  } else if (item.kind === 'booking') {
    if (!p.startsAt) throw new DomainError('intake.bad_time');
    const at = localDateTime(p.startsAt, o.timeZone);
    const minutes = p.endsAt ? Math.max(15, Math.round((Date.parse(p.endsAt) - Date.parse(p.startsAt)) / 60000)) : 45;
    const topic = clean(p.topicLabel) || clean(p.topic);
    const note = [topic, p.mode, clean(p.partner), p.note].map(clean).filter(Boolean).join(' · ');
    const locationType = bookingLocationType(p.mode);
    const location = clean(p.location) || (locationType === 'office' ? clean(o.officeAddress) : '') || null;
    let title = p.status === 'confirmed'
      ? (T.bookingConfirmed ?? 'Appointment (avrastro.com){topic}').replace('{topic}', topic ? `: ${topic}` : '')
      : (T.bookingNote ?? 'Appointment requested on avrastro.com{topic}').replace('{topic}', topic ? `: ${topic}` : '');
    const site = intakeSite(p);
    if (site !== 'avrastro.com') title = title.replace('avrastro.com', site);
    parts.push(withData(createTask(view, { kind: 'appointment', title, personId: pid, dueDate: at.date, dueTime: at.time, durationMin: minutes, locationType, location, notes: note || null, origin: 'website' }, ctx), item.ref));
  } else if (item.kind === 'lead') {
    const topic = clean(p.topicLabel) || clean(p.topic);
    const site = intakeSite(p);
    let noteTitle = (T.leadNote ?? 'Message from avrastro.com{topic}').replace('{topic}', topic ? `: ${topic}` : '');
    if (site !== 'avrastro.com') noteTitle = noteTitle.replace('avrastro.com', site);
    parts.push(addNote(view, { personId: pid, title: noteTitle, body: clean(p.message) || '—' }, ctx));
    if (p.kind === 'callback') {
      // a call task at the time of day that suits (today, or the next day when that window has already passed)
      const at = callbackTime(p.bestTime);
      const label = p.bestTime === 'morning' ? T.bestTimeMorning ?? 'morning' : p.bestTime === 'midday' ? T.bestTimeMidday ?? 'midday' : p.bestTime === 'afternoon' ? T.bestTimeAfternoon ?? 'afternoon' : '';
      const nowLocal = localDateTime(ctx.now, o.timeZone);
      const late = at !== null && nowLocal.date === ctx.today && nowLocal.time >= `${String(Number(at.slice(0, 2)) + 3).padStart(2, '0')}:00`;
      const title = (T.callbackTask ?? 'Call back {name}{topic}').replace('{name}', person.displayName).replace('{topic}', topic ? `: ${topic}` : '');
      parts.push(withData(createTask(view, { kind: 'call', title, personId: pid, dueDate: late ? addDays(ctx.today, 1) : ctx.today, dueTime: at, priority: 'high', notes: [label, site !== 'avrastro.com' ? site : ''].filter(Boolean).join(' · ') || null, origin: 'website' }, ctx), item.ref));
    } else {
      parts.push(withData(createTask(view, { kind: 'call', title: (T.leadTask ?? 'Reply to {name}').replace('{name}', person.displayName), personId: pid, dueDate: ctx.today, priority: 'high', origin: 'website' }, ctx), item.ref));
    }
  } else throw new DomainError('intake.kind');
  const cs = mergeChangesets(`intake.accept.${item.kind}`, ...parts);
  cs.puts.push(timeline(ctx, pid, 'system', `avrastro.com · ${item.kind}`, null));
  cs.entity = 'persons'; cs.entityId = pid; cs.summary = person.displayName;
  return { cs, personId: pid, created };
}

/** the citizen withdrew a share on the website: a new consent record says so (the earlier one stays as history) */
export function recordIntakeWithdrawal(v: WorkspaceView, personId: string, ref: string, status: 'revoked' | 'expired', ctx: Ctx, text?: string): Changeset {
  const already = v.where('consents', 'personId', personId).some((x) => x.purpose === 'advice' && !x.granted && (x.scope ?? '').includes(ref));
  if (already) return { action: 'consent.noop', puts: [], deletes: [] };
  return recordConsent(v, { personId, purpose: 'advice', granted: false, channel: 'website', lawfulBasis: 'consent', scope: `${ref} (${status})`, source: 'avrastro.com', notes: text ?? (status === 'expired' ? 'Share period ended on avrastro.com' : 'Withdrawn by the client on avrastro.com') }, ctx);
}

/** open appointment tasks created from a website booking (to cancel them when the client cancels) */
export function tasksForIntake(v: WorkspaceView, ref: string) {
  return v.rows('tasks').filter((t) => t.status === 'open' && (t.data as { intakeRef?: string } | null)?.intakeRef === ref);
}

function withData(cs: Changeset, ref: string): Changeset {
  const t = cs.puts.find((x) => x.table === 'tasks');
  if (t) (t.row as { data: unknown }).data = { ...((t.row as { data?: object | null }).data ?? {}), intakeRef: ref };
  return cs;
}
/** a read view that already contains the rows created earlier in the same command */
function overlay(v: WorkspaceView, parts: Changeset[]): WorkspaceView {
  const extra = parts.flatMap((p) => p.puts);
  return {
    rows: (t) => { const add = extra.filter((x) => x.table === t).map((x) => x.row); const ids = new Set(add.map((r) => (r as { id: string }).id)); return [...v.rows(t).filter((r) => !ids.has((r as { id: string }).id)), ...add] as never; },
    get: (t, id) => (extra.find((x) => x.table === t && (x.row as { id: string }).id === id)?.row as never) ?? v.get(t, id),
    where: (t, f, val) => { const add = extra.filter((x) => x.table === t && (x.row as unknown as Record<string, unknown>)[f as string] === val).map((x) => x.row); const ids = new Set(add.map((r) => (r as { id: string }).id)); return [...v.where(t, f, val).filter((r) => !ids.has((r as { id: string }).id)), ...add] as never; },
  };
}
